import { Button } from "@/components/ui/button";

import { ArrowUpDown, ChevronDown } from "lucide-react";

import {
  ColumnDef,
  VisibilityState,
  ColumnFiltersState,
  SortingState,
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";

import { useTranslation } from "react-i18next";
import { formatDate, removeLastLetter } from "@/utils";
import { cn } from "@/lib";
import { getStatusConfig } from "@/pages/schedulings/components/status-config";
import { useMemo, useState } from "react";

import {
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableHeader,
  TableCell,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  Checkbox,
  Label,
  Badge,
} from "@/components/ui";

import {
  useCreateSynchronizeAllSchedulingsMutation,
  useCreateSynchronizeScheduleMutation,
  useToast,
} from "@/hooks";
import {
  ReportScheduleResponse,
  Scheduling,
  SchedulingsResponse,
} from "@/types";

import { Pagination } from "../../pagination";
import { FormScheduling } from "../../forms/schedulings";
import { AlertDialog } from "../../dialogs";

type DataTableSchedulesProps = {
  data: SchedulingsResponse | ReportScheduleResponse;
  setPerPage?: (value: number) => void;
  perPage?: number;
};

type Colunmns = {
  id: string;
  procedures: string;
  collaborator: string;
  date: string;
  hour: string;
  status: string;
  sync: boolean;
  scheduling: Scheduling;
};

export function DataTableSchedulings({
  data: { items, data },
  setPerPage,
}: DataTableSchedulesProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});

  const {
    mutateAsync: synchronizeAllSchedulemutateAsync,
    isLoading: synchronizeAllScheduleIsLoading,
  } = useCreateSynchronizeAllSchedulingsMutation();

  const {
    mutateAsync: synchronizeSchedulemutateAsync,
    isLoading: synchronizeScheduleIsLoading,
  } = useCreateSynchronizeScheduleMutation();

  async function handleSynchronizeAllScheduleSubmit() {
    try {
      await synchronizeAllSchedulemutateAsync(undefined, {
        onSuccess: ({ message }) => {
          toast({
            title: t("LABEL.success"),
            description: t(`MESSAGE.${message.toLocaleLowerCase()}`),
          });
        },
        onError: () => {
          toast({
            title: t("LABEL.error"),
            description: t(`MESSAGE.error_message`),
            variant: "destructive",
          });
        },
      });

    } catch {
      toast({
        title: t("LABEL.error"),
        description: t(`MESSAGE.error`),
        variant: "destructive",
      });
    }
  }
  async function handleSynchronizeScheduleSubmit(id: string) {
    try {
      await synchronizeSchedulemutateAsync(id, {
        onSuccess: ({ message }) => {
          toast({
            title: t("LABEL.success"),
            description: t(`MESSAGE.${message.toLocaleLowerCase()}`),
          });
        },
        onError: () => {
          toast({
            title: t("LABEL.error"),
            description: t(`MESSAGE.error_message`),
            variant: "destructive",
          });
        },
      });

    } catch {
      toast({
        title: t("LABEL.error"),
        description: t(`MESSAGE.error`),
        variant: "destructive",
      });
    }
  }

  function getData() {
    const data = items.map((item) => ({
      id: item.id,
      procedures: item.procedure.name,
      collaborator: item.employee.name,
      patient: item.patient.name,
      date: formatDate(item.date, false, true),
      hour: `${item.hour}/${item.endtime}`,
      status: item.status,
      sync: item.isSynchronizedWithGoogle,
      scheduling: item as Scheduling,
    }));
    return data;
  }

  const columns: ColumnDef<Colunmns>[] = [
    {
      accessorKey: "procedures",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className="px-0"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("LABEL.procedures")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="capitalize">{row.getValue("procedures")}</div>
      ),
    },
    {
      accessorKey: "collaborator",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className="px-0"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {removeLastLetter(t("LABEL.collaborator"), 1)}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="capitalize">{row.getValue("collaborator")}</div>
      ),
    },
    {
      accessorKey: "patient",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className="px-0"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("LABEL.patient")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="capitalize">{row.getValue("patient")}</div>
      ),
    },
    {
      accessorKey: "date",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className="px-0"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("LABEL.date")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="tabular-nums">{row.getValue("date")}</div>
      ),
    },
    {
      accessorKey: "hour",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className="px-0"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("LABEL.hour")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="tabular-nums">{row.getValue("hour")}</div>
      ),
    },
    {
      accessorKey: "status",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            className="px-0"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Status
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const status = getStatusConfig(row.original.status, row.original.scheduling.outcome);
        return (
          <span
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium",
              status.chip
            )}
          >
            <span className={cn("size-1.5 rounded-full", status.dot)} aria-hidden="true" />
            {row.original.scheduling.outcome === "no_show" ? status.label : t(`LABEL.${row.original.status}`)}
          </span>
        );
      },
    },
  ];

  if (typeof setPerPage === "function") {
    columns.push({
      accessorKey: "sync",
      header: t("LABEL.sync"),
      cell: ({ row }) => {
        const sync = row.getValue("sync") as boolean;
        return (
          <div className="relative">
            <AlertDialog
              title={t("LABEL.are you sure?")}
              description={t("LABEL.do you want to sync with google calendar?")}
              callBack={async () =>
                handleSynchronizeScheduleSubmit(row.original.id)
              }
              isLoading={synchronizeScheduleIsLoading}
              disabled={synchronizeScheduleIsLoading || sync}
            >
              <button className="flex justify-center items-center gap-2 cursor-pointer w-full min-w-24">
                <Checkbox
                  id={`write-off-release-${row.original.id}`}
                  className="size-4 rounded-full"
                  disabled={sync}
                  checked={sync}
                />
                {!sync && (
                  <>
                    <span> {sync && <>{sync}</>} </span>
                    <span>
                      {" "}
                      {!sync && (
                        <Badge className="bg-transparent hover:bg-app-surface-muted">
                          <img
                            src="/media/svg/brand-logos/google-icon.svg"
                            alt="Google logo"
                            className="w-5 h-5"
                          />
                        </Badge>
                      )}{" "}
                    </span>
                  </>
                )}
              </button>
            </AlertDialog>
          </div>
        );
      },
    });
  }

  const dataMemo = useMemo(() => getData(), [items]);
  const columnsMemo = useMemo(() => columns, []);

  const table = useReactTable({
    data: dataMemo,
    // @ts-check
    columns: columnsMemo,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  return (
    <div className="w-full">
      <div className="flex justify-between items-center py-4">
        {typeof setPerPage === "function" && (
          <div className="flex items-center gap-x-3 max-w-sm">
            <Label>{t("LABEL.show")}</Label>
            <select
              onChange={(e) => setPerPage(parseInt(e.target.value))}
              className="h-9 rounded-lg border border-app-border bg-app-surface px-2 text-sm text-app-fg outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
            <Label>itens</Label>
          </div>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="ml-auto">
              {t("LABEL.columns")} <ChevronDown className="ml-2 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {table
              .getAllColumns()
              .filter((column) => column.getCanHide())
              .map((column) => {
                return (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    className="capitalize"
                    checked={column.getIsVisible()}
                    // onCheckedChange={(value) => column.toggleVisibility(!!value)}
                    onCheckedChange={(value) => {
                      setColumnVisibility((prev) => ({
                        ...prev,
                        [column.id]: !!value,
                      }));
                    }}
                  >
                    {t(`LABEL.${column.id}`)}
                  </DropdownMenuCheckboxItem>
                );
              })}
          </DropdownMenuContent>
        </DropdownMenu>

        {typeof setPerPage === "function" && (
          <AlertDialog
            callBack={() => handleSynchronizeAllScheduleSubmit()}
            isLoading={synchronizeAllScheduleIsLoading}
            disabled={synchronizeAllScheduleIsLoading}
            title={t("LABEL.are you sure?")}
            description={t(
              "MESSAGE.want to sync all schedules with google calendar"
            )}
          >
            <Button
              variant="secondary"
              type="button"
              className="ml-4 flex items-center gap-x-2"
              isLoading={synchronizeAllScheduleIsLoading}
              disabled={synchronizeAllScheduleIsLoading}
            >
              <span>{t("LABEL.syncAllEvents")}</span>
              <span className="indicator-label">
                <img
                  src="/media/svg/brand-logos/google-icon.svg"
                  alt="Google logo"
                  className="w-4 h-4"
                />
              </span>
            </Button>
          </AlertDialog>
        )}
      </div>
      <div className="overflow-hidden rounded-xl border border-app-border bg-app-surface">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => {
                    const isExcludedColumn =
                      cell.column.id === "sync" || cell.column.id === "select";
                    return (
                      <>
                        {!isExcludedColumn ? (
                          <TableCell key={cell.id}>
                            <FormScheduling
                              scheduling={row.original.scheduling}
                            >
                              <div className="cursor-pointer">
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </div>
                            </FormScheduling>
                          </TableCell>
                        ) : (
                          <TableCell key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        )}
                      </>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  {t("no results")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <Pagination data={data} />
    </div>
  );
}
